import { useBranchTimezone } from "../api/use-branch-timezone";
import { useMemo, useState } from "react";
import type { HrClinicCertificate } from "@dentacore/shared-types";

import {
  checkRenewal,
  createCertificate,
  deleteCertificate,
  rejectRenewal,
  renewalFileUrl,
  renewCertificate,
  updateCertificate,
} from "../api/hr";
import { describe } from "../settings/use-writer";
import type { Writer } from "../settings/use-writer";
import { Pager, useFitPages } from "../ui/paging";
import { initialsOf, localToday, roleLabel } from "./roster";
import styles from "./staff.module.css";
import { daysBetween, shortDate } from "./team";
import type { Tone } from "./team";
import own from "./team.module.css";
import {
  AddCertificateDialog,
  CheckRenewalDialog,
  EditCertificateDialog,
  RejectRenewalDialog,
} from "./compliance-dialogs";
import { RenewDialog } from "./team-dialogs";
import { Mark } from "./team-mark";

type Open =
  | { kind: "add" }
  | { kind: "edit" | "renew" | "check" | "reject"; certificate: HrClinicCertificate };

/**
 * Staff → Compliance (STORY-181d; HR-D3–HR-D5, HR-D10–HR-D12, HR-D16, HR-D17).
 *
 * **Every certificate in the clinic, one list**: a renewal waiting for a check
 * first (HR-D16), then soonest to run out, one that does not run out last.
 * A waiting row offers *Check* and *Reject*, every other *Renew*; clicking a
 * certificate edits it, with *Delete* inside (HR-D17). Every action is a
 * dialog that says what it does before it does it — a check and a rejection
 * cannot be undone, so they confirm rather than offer Undo.
 *
 * The foot names whoever has lapsed on something needed to treat and can still
 * be booked (HR-D3): DentaCore tells, the clinic decides.
 */
export function ComplianceView({
  certificates,
  people,
  canManage,
  writer,
  reload,
}: {
  /** Null while it is read. */
  certificates: readonly HrClinicCertificate[] | null;
  /** Active people, for *Add a certificate*. */
  people: readonly { userId: string; name: string }[];
  canManage: boolean;
  writer: Pick<Writer, "busy" | "run" | "fail">;
  reload: () => Promise<void>;
}): React.JSX.Element {
  const { busy, run, fail } = writer;
  const timezone = useBranchTimezone();
  const today = localToday(timezone);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Open | null>(null);
  const [follow, setFollow] = useState<{ key: string } | null>(null);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const shown = (certificates ?? []).filter(
      (row) =>
        needle === "" ||
        `${row.name} ${row.personName} ${row.role.replace(/_/g, " ")}`
          .toLowerCase()
          .includes(needle),
    );
    // Waiting first; the server's soonest-first order is kept within each half.
    return [
      ...shown.filter((row) => row.pendingRenewal !== null),
      ...shown.filter((row) => row.pendingRenewal === null),
    ];
  }, [certificates, query]);

  const fit = useFitPages({
    keys: rows.map((row) => row.id),
    estimate: () => 57,
    gap: 0,
    resetOn: query,
    follow,
  });
  const [start, end] = fit.range;

  const lapsed = (certificates ?? []).filter((row) => row.state === "EXPIRED" && row.neededToTreat);

  /** Run a write, re-read the list, follow the row to wherever it sorted, and close. */
  function act(id: string, write: () => Promise<unknown>, done: string): void {
    void run(write, reload, done).then((ok) => {
      if (!ok) return;
      setOpen(null);
      setFollow({ key: id });
    });
  }

  function openFile(certificate: HrClinicCertificate): void {
    const renewalId = certificate.pendingRenewal?.id;
    if (renewalId === undefined) return;
    renewalFileUrl(renewalId)
      .then((url) => window.open(url, "_blank", "noopener"))
      .catch((cause: unknown) => fail(describe(cause, "The document did not open.")));
  }

  return (
    <div className={own.column} data-testid="compliance-view">
      <div className={own.figures}>
        {certificates === null ? (
          <span>Reading&hellip;</span>
        ) : (
          <>
            <span data-testid="compliance-waiting">
              <b>{certificates.filter((row) => row.pendingRenewal !== null).length}</b> waiting for
              a check
            </span>
            <span className={lapsed.length > 0 ? own.figureWarm : undefined}>
              <b>{certificates.filter((row) => row.state === "EXPIRED").length}</b> expired
            </span>
            <span>
              <b>{certificates.filter((row) => row.state === "DUE").length}</b> due soon
            </span>
          </>
        )}
        <input
          type="search"
          className={`${styles.search} ${own.searchEnd}`}
          placeholder="Search certificates"
          aria-label="Search certificates"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="compliance-search"
        />
        {canManage && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => setOpen({ kind: "add" })}
            data-testid="add-certificate"
          >
            Add a certificate
          </button>
        )}
      </div>

      <div className={`${styles.body} ${own.bodyFlush}`}>
        <section className={own.card} aria-label="Certificates">
          <div className={`${own.cells} ${own.certCells} ${own.head}`}>
            <span className={own.headTitle}>
              <b>Certificates</b>
              <span className={own.count}>{rows.length}</span>
            </span>
            <span>Expires</span>
            <span>Time left</span>
            <span />
          </div>

          <div className={own.area} ref={fit.areaRef} data-testid="compliance-list">
            {certificates !== null && rows.length === 0 && (
              <p className={own.empty}>
                {query.trim() !== ""
                  ? "Nothing matches that."
                  : "No certificates yet. Add one, and DentaCore tells you when it is due."}
              </p>
            )}
            {rows.slice(start, end).map((row) => {
              const [given = "", ...family] = row.personName.split(" ");
              const waiting = row.pendingRenewal;
              return (
                <div
                  key={row.id}
                  ref={fit.measure(row.id)}
                  className={`${own.row} ${own.cells} ${own.certCells} ${canManage ? own.clickable : ""}`}
                  data-testid="compliance-row"
                  data-certificate={row.id}
                >
                  <span className={own.who}>
                    <Mark tone={toneOf(row)} />
                    <span className={own.face} data-role={row.role} aria-hidden="true">
                      {initialsOf(given, family.join(" "))}
                    </span>
                    <span className={own.name}>
                      {canManage ? (
                        <button
                          type="button"
                          className={own.nameButton}
                          onClick={() => setOpen({ kind: "edit", certificate: row })}
                          aria-label={`Edit ${row.personName}'s ${row.name}`}
                          data-testid="compliance-edit"
                        >
                          {row.name}
                        </button>
                      ) : (
                        <b>{row.name}</b>
                      )}
                      <span className={own.sub}>
                        {row.personName} · {roleLabel(row.role)}
                        {waiting !== null && (
                          <span className={own.warmText}> · renewal waiting for a check</span>
                        )}
                        {waiting === null && row.rejection !== null && " · last upload rejected"}
                      </span>
                    </span>
                  </span>
                  <span className={own.cell}>
                    {row.expiresOn === null ? "—" : shortDate(row.expiresOn)}
                  </span>
                  <span className={own.cell}>{timeLeft(row, today)}</span>
                  <span className={own.actions}>
                    {canManage && waiting !== null && (
                      <>
                        <button
                          type="button"
                          className={styles.ghost}
                          disabled={busy}
                          onClick={() => setOpen({ kind: "check", certificate: row })}
                          data-testid="check-open"
                        >
                          Check
                        </button>
                        <button
                          type="button"
                          className={styles.quiet}
                          disabled={busy}
                          onClick={() => setOpen({ kind: "reject", certificate: row })}
                          data-testid="reject-open"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {waiting?.hasFile === true && (
                      <button
                        type="button"
                        className={styles.quiet}
                        onClick={() => openFile(row)}
                        aria-label={`Open ${row.personName}'s uploaded ${row.name}`}
                        data-testid="open-file"
                      >
                        Open file
                      </button>
                    )}
                    {canManage && waiting === null && (
                      <button
                        type="button"
                        className={styles.quiet}
                        disabled={busy}
                        onClick={() => setOpen({ kind: "renew", certificate: row })}
                        aria-label={`Renew ${row.personName}'s ${row.name}`}
                        data-testid="renew-open"
                      >
                        Renew
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={own.foot}>
            {lapsed[0] !== undefined && (
              <span className={own.footWarn} data-testid="lapsed-warning">
                <Mark tone="bad" />
                <span>
                  <b>{lapsed[0].personName}</b> has no valid {lapsed[0].name} and can still be
                  booked
                  {lapsed.length > 1 && `, and ${String(lapsed.length - 1)} more like it`}.
                </span>
              </span>
            )}
            <Pager
              page={fit.page}
              count={fit.pages.length}
              onPage={fit.setPage}
              from={start + 1}
              to={end}
              total={rows.length}
              noun="certificates"
              testId="compliance-pager"
            />
          </div>
        </section>
      </div>

      {open?.kind === "add" && (
        <AddCertificateDialog
          people={people}
          busy={busy}
          onSave={(userId, body) => {
            void run(() => createCertificate(userId, body), reload, "Certificate added.").then(
              (ok) => {
                if (ok) setOpen(null);
              },
            );
          }}
          onClose={() => setOpen(null)}
        />
      )}

      {open?.kind === "edit" && (
        <EditCertificateDialog
          certificate={open.certificate}
          busy={busy}
          onSave={(body) => {
            if (Object.keys(body).length === 0) {
              setOpen(null);
              return;
            }
            act(open.certificate.id, () => updateCertificate(open.certificate.id, body), "Saved.");
          }}
          onDelete={() =>
            act(
              open.certificate.id,
              () => deleteCertificate(open.certificate.id),
              `${open.certificate.name} deleted.`,
            )
          }
          onClose={() => setOpen(null)}
        />
      )}

      {open?.kind === "renew" && (
        <RenewDialog
          name={open.certificate.personName}
          certificate={open.certificate}
          countsAtOnce
          busy={busy}
          onSave={(expiresOn, file) =>
            act(
              open.certificate.id,
              () => renewCertificate(open.certificate.id, expiresOn, file),
              `${open.certificate.name} renewed.`,
            )
          }
          onClose={() => setOpen(null)}
        />
      )}

      {open?.kind === "check" && open.certificate.pendingRenewal !== null && (
        <CheckRenewalDialog
          certificate={open.certificate}
          busy={busy}
          onOpenFile={
            open.certificate.pendingRenewal.hasFile ? () => openFile(open.certificate) : null
          }
          onCheck={(expiresOn) =>
            act(
              open.certificate.id,
              () => checkRenewal(open.certificate.pendingRenewal?.id ?? "", expiresOn),
              `${open.certificate.name} checked — the new date is in force.`,
            )
          }
          onClose={() => setOpen(null)}
        />
      )}

      {open?.kind === "reject" && open.certificate.pendingRenewal !== null && (
        <RejectRenewalDialog
          certificate={open.certificate}
          busy={busy}
          onReject={(reason) =>
            act(
              open.certificate.id,
              () => rejectRenewal(open.certificate.pendingRenewal?.id ?? "", reason),
              `Rejected. ${open.certificate.personName} will see why.`,
            )
          }
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function toneOf(row: HrClinicCertificate): Tone {
  return row.state === "EXPIRED" ? "bad" : row.state === "DUE" ? "warm" : "calm";
}

/** `8 days to run`, `3 days ago · still bookable`, or `does not run out`. */
function timeLeft(row: HrClinicCertificate, today: string): React.JSX.Element {
  if (row.expiresOn === null) return <span className={own.sub}>does not run out</span>;
  const ahead = daysBetween(today, row.expiresOn);
  const word = (count: number): string => `${String(count)} ${count === 1 ? "day" : "days"}`;
  if (ahead < 0) {
    return (
      <>
        <span className={own.warmText}>{word(-ahead)} ago</span>
        <span className={own.sub}>{row.neededToTreat ? "still bookable" : "lapsed"}</span>
      </>
    );
  }
  return (
    <>
      <span className={row.state === "DUE" ? own.warmText : undefined}>{word(ahead)}</span>
      <span className={own.sub}>to run</span>
    </>
  );
}
